// Shipping address confirmation step in the cart checkout flow
import { useGooglePlacesAutocomplete } from "@/lib/hooks/use-google-places-autocomplete";
import { PHONE_ERROR_MESSAGE, PHONE_REGEX } from "@/lib/validations";
import {
  COMMON_COUNTRIES,
  getProvincesForCountry,
  normalizeCountryCode,
  type MemberAddress,
} from "@/lib/queries/member-data";
import { useForm } from "@tanstack/react-form";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { FormControl, FormItem, FormLabel, FormMessage } from "@repo/ui/form";
import { Loader2, MapPin } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

interface ShippingAddressStepProps {
  address: MemberAddress | null;
  onAddressChange: (address: MemberAddress) => void;
}

const emptyAddress: MemberAddress = {
  firstName: "",
  lastName: "",
  address1: "",
  address2: "",
  city: "",
  country: "",
  province: "",
  zip: "",
  phone: "",
};

// EPD-11401: enforce that country/province persist as ACCEPTED (ISO) values, per the
// ticket's expected behavior ("only allow accepted city, state, and country formats").
// A localized/free-typed value (e.g. "Estados Unidos") fails validation with a clear
// message instead of silently shipping an order that fulfillment later rejects.
// Exported for unit testing the EPD-11401 accepted-format enforcement.
export const shippingAddressSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    address1: z.string().min(1, "Street address is required"),
    address2: z.string(),
    city: z.string().min(1, "City is required"),
    country: z.string().min(1, "Country is required"),
    province: z.string(),
    zip: z.string().min(1, "Postal code is required"),
    phone: z
      .string()
      .min(1, "Phone number is required")
      .regex(PHONE_REGEX, PHONE_ERROR_MESSAGE),
  })
  .superRefine((values, ctx) => {
    const country = values.country?.trim() ?? "";
    const isKnownCountry = COMMON_COUNTRIES.some(
      (c) => c.code === country.toUpperCase(),
    );
    if (country && !isKnownCountry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["country"],
        message: "Please select a valid country",
      });
    }
    // Only validate the province against the country's subdivision list when that
    // country actually has one; countries without subdivisions accept an empty value.
    if (isKnownCountry) {
      const provinces = getProvincesForCountry(country.toUpperCase());
      const province = values.province?.trim() ?? "";
      if (provinces.length > 0) {
        const isKnownProvince = provinces.some(
          (p) => p.code.toLowerCase() === province.toLowerCase(),
        );
        if (!isKnownProvince) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["province"],
            message: "Please select a valid state/province",
          });
        }
      }
    }
  });

function isAddressFormComplete(values: MemberAddress): boolean {
  return !!(
    values.firstName &&
    values.lastName &&
    values.address1 &&
    values.city &&
    values.country &&
    values.zip &&
    values.phone
  );
}

/** Extract a Zod error message from a TanStack Form field error entry. */
function getErrorMessage(
  error: string | { message: string } | undefined,
): string {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (typeof error === "object" && "message" in error) return error.message;
  return "";
}

/**
 * Map a state/province value to its ISO code. EPD-11401: Google Places autocomplete
 * now yields the ISO code ("CA"), while saved/legacy addresses may still hold a display
 * name ("California") — so match by code first, then by name for backward compatibility.
 */
export function resolveProvinceCode(
  countryCode: string,
  stateNameOrCode: string,
): string {
  const provinces = getProvincesForCountry(countryCode);
  const value = stateNameOrCode.trim();
  const match =
    provinces.find((p) => p.code.toLowerCase() === value.toLowerCase()) ??
    provinces.find((p) => p.name.toLowerCase() === value.toLowerCase());
  return match?.code ?? value;
}

export function ShippingAddressStep({
  address,
  onAddressChange,
}: ShippingAddressStepProps) {
  const raw = address ?? emptyAddress;
  const initial: MemberAddress = {
    ...raw,
    country: normalizeCountryCode(raw.country),
    province: raw.province
      ? resolveProvinceCode(normalizeCountryCode(raw.country), raw.province)
      : "",
  };
  const [isEditing, setIsEditing] = useState(
    () => !isAddressFormComplete(initial),
  );

  const form = useForm({
    defaultValues: initial,
    validators: {
      onSubmit: shippingAddressSchema,
    },
    onSubmit: ({ value }) => {
      onAddressChange(value);
      setIsEditing(false);
    },
  });

  // Google Places autocomplete
  const {
    predictions,
    isLoading: isLoadingPredictions,
    fetchPredictions,
    selectPrediction,
    clearPredictions,
  } = useGooglePlacesAutocomplete();

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addressLine2Ref = useRef<HTMLInputElement>(null);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, []);

  const handleAddressSelect = useCallback(
    async (placeId: string) => {
      const parsed = await selectPrediction(placeId);
      if (parsed) {
        const countryCode = normalizeCountryCode(parsed.country);
        const provinceCode = resolveProvinceCode(countryCode, parsed.state);

        form.setFieldValue("address1", parsed.addressLine1);
        form.setFieldValue("address2", parsed.addressLine2);
        form.setFieldValue("city", parsed.city);
        form.setFieldValue("country", countryCode);
        form.setFieldValue("province", provinceCode);
        form.setFieldValue("zip", parsed.zip);

        // Focus apt/suite field per Google best practice
        const FOCUS_DELAY_MS = 100;
        setTimeout(() => addressLine2Ref.current?.focus(), FOCUS_DELAY_MS);
      }
      setAutocompleteOpen(false);
      clearPredictions();
    },
    [selectPrediction, clearPredictions, form],
  );

  // Sync form values when parent address changes (e.g. selecting a saved address)
  useEffect(() => {
    const next = address ?? emptyAddress;
    // Normalize country/province to ISO codes so the Select dropdowns match
    const countryCode = normalizeCountryCode(next.country);
    const provinceCode = next.province
      ? resolveProvinceCode(countryCode, next.province)
      : "";
    const normalized = {
      ...next,
      country: countryCode,
      province: provinceCode,
    };
    form.reset(normalized);
    clearPredictions();
    if (!isAddressFormComplete(normalized)) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setIsEditing(true);
    } else {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setIsEditing(false);
    }
  }, [address, form, clearPredictions]);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="w-full flex items-center justify-between p-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
          <h4 className="font-medium text-sm text-foreground">
            Shipping Address
          </h4>
        </div>
        {!isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-8 text-xs"
          >
            Edit
          </Button>
        )}
      </div>

      {/* Content - Summary or Form */}
      {isEditing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="px-4 pb-4 pt-4"
        >
          <div className="space-y-4">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="firstName"
                children={(field) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="First name"
                        className={
                          field.state.meta.errors.length > 0
                            ? "border-destructive"
                            : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <form.Field
                name="lastName"
                children={(field) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="Last name"
                        className={
                          field.state.meta.errors.length > 0
                            ? "border-destructive"
                            : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>

            {/* Address line 1 with Google Places autocomplete */}
            <Popover
              open={autocompleteOpen && predictions.length > 0}
              onOpenChange={setAutocompleteOpen}
            >
              <form.Field
                name="address1"
                children={(field) => (
                  <FormItem>
                    <FormLabel>Street Address</FormLabel>
                    <PopoverAnchor asChild>
                      <div className="relative">
                        <FormControl>
                          <Input
                            value={field.state.value}
                            autoComplete="one-time-code"
                            placeholder="Start typing to search..."
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={
                              autocompleteOpen && predictions.length > 0
                            }
                            onBlur={() => {
                              field.handleBlur();
                              blurTimeoutRef.current = setTimeout(() => {
                                setAutocompleteOpen(false);
                              }, 200);
                            }}
                            onFocus={() => {
                              if (blurTimeoutRef.current) {
                                clearTimeout(blurTimeoutRef.current);
                                blurTimeoutRef.current = null;
                              }
                              if (predictions.length > 0) {
                                setAutocompleteOpen(true);
                              }
                            }}
                            onChange={(e) => {
                              field.handleChange(e.target.value);
                              if (e.target.value.length >= 3) {
                                fetchPredictions(e.target.value);
                                setAutocompleteOpen(true);
                              } else {
                                clearPredictions();
                                setAutocompleteOpen(false);
                              }
                            }}
                            className={
                              field.state.meta.errors.length > 0
                                ? "border-destructive"
                                : undefined
                            }
                          />
                        </FormControl>
                        {isLoadingPredictions && (
                          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                        )}
                      </div>
                    </PopoverAnchor>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <Command shouldFilter={false}>
                  <CommandList>
                    <CommandEmpty>No addresses found.</CommandEmpty>
                    <CommandGroup>
                      {predictions.map((prediction) => (
                        <CommandItem
                          key={prediction.placeId}
                          value={prediction.placeId}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (blurTimeoutRef.current) {
                              clearTimeout(blurTimeoutRef.current);
                              blurTimeoutRef.current = null;
                            }
                          }}
                          onSelect={() => {
                            handleAddressSelect(prediction.placeId);
                          }}
                        >
                          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {prediction.mainText}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {prediction.secondaryText}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Address line 2 */}
            <form.Field
              name="address2"
              children={(field) => (
                <FormItem>
                  <FormLabel>
                    Apt, suite, etc.{" "}
                    <span className="text-muted-foreground font-normal">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      ref={addressLine2Ref}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Apt, suite, unit..."
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* City */}
            <form.Field
              name="city"
              children={(field) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="City"
                      className={
                        field.state.meta.errors.length > 0
                          ? "border-destructive"
                          : undefined
                      }
                    />
                  </FormControl>
                  <FormMessage>
                    {getErrorMessage(field.state.meta.errors[0])}
                  </FormMessage>
                </FormItem>
              )}
            />

            {/* Country and Province */}
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="country"
                children={(field) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Select
                        value={field.state.value}
                        onValueChange={(value: string) => {
                          if (value === field.state.value) return;
                          field.handleChange(value);
                          form.setFieldValue("province", "");
                        }}
                      >
                        <SelectTrigger
                          className={
                            field.state.meta.errors.length > 0
                              ? "border-destructive"
                              : undefined
                          }
                        >
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent position="item-aligned">
                          {COMMON_COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <form.Subscribe
                selector={(state) => state.values.country}
                children={(country) => {
                  const provinces = getProvincesForCountry(country);
                  const provinceLabel =
                    country === "US"
                      ? "State"
                      : country === "CA"
                        ? "Province"
                        : "State/Province";

                  return (
                    <form.Field
                      name="province"
                      children={(field) => (
                        <FormItem>
                          <FormLabel>{provinceLabel}</FormLabel>
                          <FormControl>
                            {provinces.length > 0 ? (
                              <Select
                                value={field.state.value}
                                onValueChange={(value: string) =>
                                  field.handleChange(value)
                                }
                              >
                                <SelectTrigger
                                  className={
                                    field.state.meta.errors.length > 0
                                      ? "border-destructive"
                                      : undefined
                                  }
                                >
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent position="item-aligned">
                                  {provinces.map((province) => (
                                    <SelectItem
                                      key={province.code}
                                      value={province.code}
                                    >
                                      {province.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                placeholder="State/Province"
                                disabled={!country}
                                className={
                                  field.state.meta.errors.length > 0
                                    ? "border-destructive"
                                    : undefined
                                }
                              />
                            )}
                          </FormControl>
                          <FormMessage>
                            {getErrorMessage(field.state.meta.errors[0])}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  );
                }}
              />
            </div>

            {/* ZIP and Phone */}
            <div className="grid grid-cols-2 gap-4">
              <form.Subscribe
                selector={(state) => state.values.country}
                children={(country) => (
                  <form.Field
                    name="zip"
                    children={(field) => (
                      <FormItem>
                        <FormLabel>
                          {country === "US" ? "ZIP Code" : "Postal Code"}
                        </FormLabel>
                        <FormControl>
                          <Input
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={
                              country === "US" ? "ZIP code" : "Postal code"
                            }
                            className={
                              field.state.meta.errors.length > 0
                                ? "border-destructive"
                                : undefined
                            }
                          />
                        </FormControl>
                        <FormMessage>
                          {getErrorMessage(field.state.meta.errors[0])}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                )}
              />
              <form.Field
                name="phone"
                children={(field) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="Phone number"
                        className={
                          field.state.meta.errors.length > 0
                            ? "border-destructive"
                            : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>

            {/* Done Button */}
            <div className="flex justify-end pt-2">
              <Button type="submit" size="sm">
                Done
              </Button>
            </div>
          </div>
        </form>
      ) : (
        <div className="px-4 pb-4 pt-4 text-sm">
          <p className="font-medium">
            {form.state.values.firstName} {form.state.values.lastName}
          </p>
          <p>{form.state.values.address1}</p>
          {form.state.values.address2 && <p>{form.state.values.address2}</p>}
          <p>
            {form.state.values.city}, {form.state.values.province}{" "}
            {form.state.values.zip}
          </p>
          <p>{form.state.values.country}</p>
          {form.state.values.phone && (
            <p className="mt-1">{form.state.values.phone}</p>
          )}
        </div>
      )}
    </div>
  );
}
